import { BeaconApiError, type ProblemDetails } from './errors.js';

export interface BeaconClientOptions {
  baseUrl: string;
  /**
   * Optional CSRF token header — required for state-changing requests
   * once full CSRF protection is wired (SPEC.md §14.5 OWASP).
   */
  csrfToken?: string | (() => string | undefined);
  /**
   * Dev-only: a user id sent as `X-Beacon-Dev-User` so the worker
   * resolves the viewer without requiring a real session. Honoured
   * only when the worker has `ENVIRONMENT=development`.
   */
  devUserId?: string | (() => string | undefined);
  /** Custom fetch — overridable for testing. */
  fetch?: typeof globalThis.fetch;
}

export class BeaconClient {
  public readonly baseUrl: string;
  private readonly fetchImpl: typeof globalThis.fetch;
  private readonly csrfToken?: string | (() => string | undefined);
  private readonly devUserId?: string | (() => string | undefined);

  constructor(opts: BeaconClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.csrfToken = opts.csrfToken;
    this.devUserId = opts.devUserId;
  }

  async request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') {
      const token =
        typeof this.csrfToken === 'function'
          ? this.csrfToken()
          : this.csrfToken;
      if (token) headers['X-CSRF-Token'] = token;
    }

    const devUser =
      typeof this.devUserId === 'function'
        ? this.devUserId()
        : this.devUserId;
    if (devUser) headers['X-Beacon-Dev-User'] = devUser;

    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (!res.ok) {
      const problem = (await safeJson(res)) as ProblemDetails | null;
      throw new BeaconApiError(
        problem ?? {
          type: 'about:blank',
          title: res.statusText,
          status: res.status,
        }
      );
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  get<T>(path: string) {
    return this.request<T>('GET', path);
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body);
  }
  patch<T>(path: string, body?: unknown) {
    return this.request<T>('PATCH', path, body);
  }
  delete<T>(path: string) {
    return this.request<T>('DELETE', path);
  }

  /**
   * POST a raw FormData body. Used for endpoints that accept multipart
   * (file uploads). We deliberately skip JSON.stringify and let the
   * browser set the Content-Type header so the multipart boundary is
   * generated correctly.
   */
  async postRaw<T>(path: string, body: FormData): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    const token =
      typeof this.csrfToken === 'function'
        ? this.csrfToken()
        : this.csrfToken;
    if (token) headers['X-CSRF-Token'] = token;

    const devUser =
      typeof this.devUserId === 'function'
        ? this.devUserId()
        : this.devUserId;
    if (devUser) headers['X-Beacon-Dev-User'] = devUser;

    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body,
      credentials: 'include',
    });

    if (!res.ok) {
      const problem = (await safeJson(res)) as ProblemDetails | null;
      throw new BeaconApiError(
        problem ?? {
          type: 'about:blank',
          title: res.statusText,
          status: res.status,
        }
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
